import { useState } from 'react';
import type { intelligence } from '@cwos/api-client';
import { TableCard, type Column } from '@cwos/ui-admin';
import { ResultCellView } from './ResultCellView.js';
import { sortRows, type TableSort } from './format.js';
import { itemEvidenceLines } from './evidence.js';

/**
 * The list answers: staff, supervision issues, missed shifts, documentation,
 * mileage, exceptions. One typed payload shape, one table.
 *
 * Reuses the house `TableCard` — real `<table>` semantics, the app's row rules
 * and hover, horizontal scroll inside the card — rather than growing a second
 * table with the same job. The sort control sits ABOVE the table rather than in
 * the headers, because `TableCard`'s headers are text by contract and putting a
 * button inside one would mean forking the shared component to serve one
 * screen.
 *
 * SORT ONLY WHERE THE PAYLOAD SAYS SO. A column is offered for sorting only
 * when the server marked it `sortable`. Ordering a column the payload cannot
 * order honestly — a mixed column, a partially-computed one — would present an
 * arrangement to the reader as a ranking the server never asserted. And when
 * sorting does run, withheld and absent cells sink to the bottom in BOTH
 * directions (see `format.ts` `sortRows`): they have no place on a scale, and
 * floating them to the top of a descending column would read as "the largest".
 *
 * EMPTY IS AN ANSWER, NOT A BLANK. An empty table renders the server's own
 * `emptyMessage` when it sent one, and otherwise says plainly that the payload
 * did not distinguish "nothing matched" from "nothing here is yours to see" —
 * because it did not, and inventing the friendlier of the two would be the
 * withheld-as-absent inversion this product ranks first.
 *
 * Absent payload → renders nothing.
 */
export function TableResult({ table }: { table: intelligence.ResultTable | null | undefined }) {
  const [sort, setSort] = useState<TableSort | null>(null);

  if (table === null || table === undefined) return null;

  const sortable = table.columns.filter((column) => column.sortable);
  const rows = sortRows(table.rows, sort);

  const columns: Column<intelligence.ResultRow>[] = table.columns.map((column) => ({
    key: column.key,
    header: column.header,
    numeric: column.numeric,
    render: (row) => <ResultCellView cell={row.cells[column.key]} />,
  }));

  return (
    <section className="rn-card" aria-label={table.title}>
      <div className="rn-card-head">
        <h3 className="rn-card-title">{table.title}</h3>
        {sortable.length > 0 && table.rows.length > 1 ? (
          <SortControl columns={sortable} sort={sort} onChange={setSort} />
        ) : null}
      </div>

      {table.caption !== undefined ? (
        <p className="rn-muted" style={{ marginTop: 0 }}>
          {table.caption}
        </p>
      ) : null}

      {table.rows.length === 0 ? (
        <p className="rn-muted" style={{ margin: 0 }} role="status">
          {table.emptyMessage ??
            'Nothing came back for this. The answer did not say whether that is because nothing matched or because nothing here is yours to see, so neither is claimed.'}
        </p>
      ) : (
        <TableCard
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          {...(table.caption !== undefined ? { caption: table.caption } : {})}
        />
      )}

      <SuppressionNote disclosure={table.disclosure} />
      <TableEvidence evidence={table.evidence} />
    </section>
  );
}

/**
 * Sorting as an explicit control with a named column and a named direction,
 * rather than an invisible affordance on a header.
 *
 * `aria-pressed` carries the current state, so the control announces which
 * arrangement is in force — a table silently re-ordered is a table whose order
 * a reader may mistake for the server's.
 */
function SortControl({
  columns,
  sort,
  onChange,
}: {
  columns: readonly intelligence.ResultColumn[];
  sort: TableSort | null;
  onChange: (sort: TableSort | null) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-8)' }}>
      <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
        Sort
      </span>
      {columns.map((column) => {
        const active = sort?.key === column.key;
        const direction = active && sort.direction === 'asc' ? 'desc' : 'asc';
        return (
          <button
            key={column.key}
            type="button"
            className="rn-link"
            aria-pressed={active}
            onClick={() => onChange({ key: column.key, direction })}
          >
            {column.header}
            {active ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
          </button>
        );
      })}
      {sort !== null ? (
        <button type="button" className="rn-link" onClick={() => onChange(null)}>
          Clear
        </button>
      ) : null}
    </div>
  );
}

/**
 * Why some cells say "Withheld".
 *
 * Said once under the table rather than in every cell, and only when the
 * payload actually declares suppression — a note about a floor that did not
 * bite would teach readers to ignore the note that matters. The cohort floor is
 * published by the server precisely so a UI can explain a blank instead of
 * leaving the reader to guess it is a zero.
 */
function SuppressionNote({
  disclosure,
}: {
  disclosure: intelligence.ResultDisclosure | undefined;
}) {
  const suppression = disclosure?.suppression;
  if (suppression === undefined || suppression.cells.length === 0) return null;
  return (
    <p className="rn-muted" style={{ margin: 'var(--space-10) 0 0' }}>
      Cells marked <em>Withheld</em> stand on fewer than {suppression.minCohortSize} people, so the
      figure is not shown. It is withheld, not zero.
    </p>
  );
}

/**
 * Classed evidence, when the payload carries it. Counts and names only.
 *
 * The class used to render as its own enum value — `operational`, `policy`,
 * `uploaded`, `catalog` — which is the envelope's partition name, not a word
 * for a source. It now renders as the noun for what was consulted, and a label
 * that turns out to be an identifier rather than plain words is replaced by
 * that same noun rather than shown (`evidence.ts`, `looksLikeIdentifier`).
 */
function TableEvidence({
  evidence,
}: {
  evidence: readonly intelligence.ResultEvidenceItem[] | undefined;
}) {
  if (evidence === undefined || evidence.length === 0) return null;
  const lines = itemEvidenceLines(evidence);
  return (
    <details style={{ marginTop: 'var(--space-10)' }}>
      <summary className="rn-link" style={{ cursor: 'pointer' }}>
        What this table is based on
      </summary>
      <ul style={{ margin: 'var(--space-8) 0 0' }}>
        {lines.map((line) => (
          <li key={line.key} className="rn-muted">
            <span style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.04em' }}>
              {line.eyebrow}
            </span>{' '}
            — {line.text}
          </li>
        ))}
      </ul>
    </details>
  );
}
